import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { useTranslate } from '@growtherapy/i18n';

import { EditIntentionModalConnected, IntentionSetModal } from '@grow-therapy-team/seedling-components';

import { SessionIntention__CreatedBy, SessionIntention__EditedBy } from '../../hooks/intentions/types';
import { useGetCurrentIntentionQuery } from '../../hooks/intentions/useGetCurrentIntentionQuery';
import { useMomentumPilotPatientAccessQuery } from '../../hooks/intentions/useMomentumPilotPatientAccessQuery';
import { useAppDependenciesContext } from '../../state';
import { useSendSessionIntentionEditedCallback } from '../messages/useSendSessionIntentionEditedCallback';
import { inSessionPatientInformationAtom } from '../state';
import { PROVIDER_SET_INTENTIONS_KEY } from './constants';
import { useUpdateSessionIntentionMutation } from './useUpdateSessionIntentionMutation';

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return <BaseErrorBoundary FallbackComponent={() => null}>{children}</BaseErrorBoundary>;
}

export const IntentionSetModalWrapper = ({
  isOpen,
  onClose,
  clientName,
  initials,
  appVersion,
  clientUserShortId,
  patientShortId,
  providerShortId,
  appointmentShortId,
}: {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  initials: string;
  appVersion: string;
  clientUserShortId: string;
  patientShortId: string;
  providerShortId: string;
  appointmentShortId: string;
}) => {
  const { logger, toast } = useAppDependenciesContext();
  const t = useTranslate();
  const { pilotAccess, loading: pilotAccessLoading } = useMomentumPilotPatientAccessQuery(
    patientShortId,
    PROVIDER_SET_INTENTIONS_KEY,
    appVersion,
  );
  const { currentIntention, loading, refetch } = useGetCurrentIntentionQuery(
    clientUserShortId,
    providerShortId,
    appointmentShortId,
  );
  const [updateSessionIntention] = useUpdateSessionIntentionMutation();
  const sendSessionIntentionEdited = useSendSessionIntentionEditedCallback();
  const inSessionPatientInfo = useAtomValue(inSessionPatientInformationAtom);
  const intentionEditedAt = inSessionPatientInfo?.intentionEditedAt;

  useEffect(
    function refetchEditedIntention() {
      if (intentionEditedAt) {
        refetch();
      }
    },
    [intentionEditedAt, refetch],
  );

  if (pilotAccessLoading || loading || !currentIntention) {
    return <></>;
  }

  const intentionLastEditedByClient =
    currentIntention.editedBy && currentIntention.editedBy === SessionIntention__EditedBy.CLIENT;
  /**
   * Providers can edit the intention if:
   * - the feature flag is on
   * - the intention was created by the provider
   * - client hasn't edited it yet
   */
  const showEditIntentionModal =
    pilotAccess?.enabled &&
    currentIntention.createdBy === SessionIntention__CreatedBy.PROVIDER &&
    !intentionLastEditedByClient;

  const handleUpdateIntention = async (intention: string) => {
    try {
      const result = await updateSessionIntention({
        variables: {
          sessionIntentionShortId: currentIntention.shortId,
          contentText: intention,
        },
      });

      const shortId = result.data?.updateSessionIntention?.sessionIntention?.shortId;
      if (shortId) {
        await sendSessionIntentionEdited(shortId);
        onClose();
      }
    } catch (error) {
      logger.error('Error editing session intention', { error });
      toast.error(t('telehealth-core:sessionIntention.couldNotEditIntentionForClient'));
    }
  };

  return (
    <ErrorBoundary>
      {showEditIntentionModal ? (
        <EditIntentionModalConnected
          appointmentShortId={appointmentShortId}
          sessionIntentionShortId={currentIntention.shortId}
          isOpen={isOpen}
          onClose={onClose}
          onSaveIntention={handleUpdateIntention}
          name={clientName}
          intention={currentIntention.contentText}
          initials={initials}
        />
      ) : (
        <IntentionSetModal
          isOpen={isOpen}
          onClose={onClose}
          name={clientName}
          initials={initials}
          intention={currentIntention.contentText}
        />
      )}
    </ErrorBoundary>
  );
};
